import YearFields from '../components/YearFields';
import { datingDraft, datingPayload } from '../utils/dating';
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from '../api';
import SearchableSelect from '../components/SearchableSelect';
import RichTextEditor from "../components/RichTextEditor";
import PhotoUploader, { uploadButtonLabel, usePhotoUploads } from '../components/PhotoUploader';
import ThumbnailImage from '../components/ThumbnailImage';

export default function EditDetailPage() {
  const [dating, setDating] = useState(() => datingDraft());
  const { id } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);

  const [manufacturers, setManufacturers] = useState([]);
  const [stamps, setStamps] = useState([]);
  const [products, setProducts] = useState([]);
  const [types, setTypes] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);

  const uploads = usePhotoUploads();
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const [subtypeId, setSubtypeId] = useState('');
  const [availableSubtypes, setAvailableSubtypes] = useState([]);

  const uploadHost = process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : '';

  const BELONGS_TO_OPTIONS = [
    { id: 'максим', name: 'Максим' },
    { id: 'станок', name: 'Станок' },
    { id: 'ЗИП', name: 'ЗИП' },
  ];

  // ── Load data ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const [dRes, mRes, sRes, pRes, tRes] = await Promise.all([
          api.get(`/details/${id}`),
          api.get('/manufacturers'),
          api.get('/stamps'),
          api.get('/products'),
          api.get('/detailTypes'),
        ]);

        if (!mounted) return;

        setManufacturers(mRes.data || []);
        setStamps(sRes.data || []);
        setProducts(pRes.data || []);
        setTypes(tRes.data || []);

        const d = dRes.data;
        setDating(datingDraft(d));
        setDetail({
          id: d.id,
          name: d.name || '',
          description: d.description || '',
          source: d.source || '',
          number: d.number || '',
          year: d.year || '',
          subtype: d.subtype || null,
          manufacturer: d.manufacturer || null,
          product: d.product || null,
          type: d.type || null,
          belongsTo: d.type?.belongsTo || d.belongsTo || '',
          stamps: d.stamps || [],
          photos: d.photos || [],
        });

        setSubtypeId(d.subtype?.id ? d.subtype.id : '');
      } catch (e) {
        console.error(e);
      }
    };

    load();
    return () => { mounted = false; };
  }, [id]);

  // ── Derive filtered types by belongsTo ──────────────────────────────────────
  useEffect(() => {
    if (!detail?.belongsTo) {
      setFilteredTypes(types);
      return;
    }
    const filtered = types.filter(t => t.belongsTo === detail.belongsTo);
    setFilteredTypes(filtered);
  }, [detail?.belongsTo, types]);

  // ── Subtypes for selected type ───────────────────────────────────────────────
  useEffect(() => {
    if (!detail?.type?.id) {
      setAvailableSubtypes([]);
      setSubtypeId('');
      return;
    }
    const selectedType = types.find(t => t.id === detail.type.id);
    if (selectedType?.subtypes?.length > 0) {
      setAvailableSubtypes(selectedType.subtypes);
      if (subtypeId && !selectedType.subtypes.some(st => st.id === subtypeId)) {
        setSubtypeId('');
      }
    } else {
      setAvailableSubtypes([]);
      setSubtypeId('');
    }
  }, [detail?.type, types]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    setDetail(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleStampsChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions).map(o => parseInt(o.value, 10));
    setDetail(prev => ({ ...prev, stamps: selectedOptions.map(id => ({ id })) }));
  };

  const handleRemovePhoto = (photoId) => {
    setDetail(prev => ({
      ...prev,
      photos: prev.photos.filter(p => p.id !== photoId)
    }));
  };

  const handleSearchPhotos = async () => {
    if (searchTerm.trim().length === 0) return;
    const res = await api.get(`/photos/search?q=${encodeURIComponent(searchTerm)}`);
    setSearchResults(res.data);
  };

  const handleAttachPhoto = (photo) => {
    setDetail(prev => {
      if (prev.photos?.some(p => p.id === photo.id)) return prev;
      return { ...prev, photos: [...(prev.photos || []), photo] };
    });
  };

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uploads.busy || saving) return;
    if (uploads.failedCount && !window.confirm('Часть фото не загрузилась. Сохранить без них?')) return;

    setSaving(true);
    try {
      datingPayload(dating);
      const allPhotos = [
        ...(detail.photos || []),
        ...uploads.photos.filter(photo => !(detail.photos || []).some(p => p.id === photo.id))
      ].map(p => ({ id: p.id }));

      await api.put(`/details/${id}`, {
        ...detail,
        ...datingPayload(dating),
        manufacturer: detail.manufacturer?.id ? { id: detail.manufacturer.id } : null,
        product: detail.product?.id ? { id: detail.product.id } : null,
        type: detail.type?.id ? { id: detail.type.id } : null,
        subtype: subtypeId ? { id: subtypeId } : null,
        belongsTo: detail.belongsTo || null,
        photos: allPhotos
      });

      navigate(`/details/${id}`);
    } catch (error) {
      console.error('Ошибка при сохранении:', error);
      alert(error.message || 'Не удалось сохранить изменения');
      setSaving(false);
    }
  };

  if (!detail) return <div className="text-white p-6">Загрузка...</div>;

  return (
    <form onSubmit={handleSubmit} className="min-h-screen bg-gray-900 text-white">
      <div className="p-6 max-w-2xl mx-auto text-white">
        <h1 className="text-2xl mb-4">Редактировать деталь</h1>

        <label className="block mb-2">Название</label>
        <input
          name="name"
          value={detail.name}
          onChange={handleChange}
          className="w-full mb-4 p-2 bg-gray-800 rounded border border-gray-700"
          placeholder="Название детали"
        />

        <div className="mb-4">
          <label className="block mb-2">Описание</label>
          <RichTextEditor
            value={detail.description || ''}
            onChange={(val) => setDetail(prev => ({ ...prev, description: val }))}
            placeholder="Введите описание"
          />
        </div>

        <label className="block mb-2">Источник</label>
        <input
          name="source"
          value={detail.source}
          onChange={handleChange}
          className="w-full mb-4 p-2 bg-gray-800 rounded border border-gray-700"
          placeholder="Источник (ссылка/описание)"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>

            <YearFields value={dating} onChange={setDating} />
          </div>

          <div>
            <label className="block mb-2">Серийный номер</label>
            <input
              name="number"
              value={detail.number}
              onChange={handleChange}
              className="w-full p-2 bg-gray-800 rounded border border-gray-700"
              placeholder="№ детали"
            />
          </div>
        </div>

        {/* BelongsTo */}
        <div className="mt-4">
          <label className="block mb-2">Назначение (belongsTo) (обязательное поле)</label>
          <SearchableSelect
            options={BELONGS_TO_OPTIONS}
            value={detail.belongsTo || ''}
            onChange={(val) => {
              setDetail(prev => ({
                ...prev,
                belongsTo: val || '',
                type: null,
              }));
              setSubtypeId('');
            }}
            placeholder="— Выбери назначение —"
          />
        </div>

        {/* Type */}
        <div className="mt-4">
          <label className="block mb-2">Тип детали (обязательное поле)</label>
          <SearchableSelect
            options={filteredTypes}
            value={detail.type?.id || ''}
            onChange={(val) => {
              const parsed = typeof val === 'string' ? parseInt(val, 10) : val;
              setDetail(prev => ({
                ...prev,
                type: parsed ? { id: parsed } : null
              }));
            }}
            placeholder="— Выбери тип —"
          />
        </div>

        {/* Subtype (optional) */}
        {availableSubtypes.length > 0 && (
          <div className="mt-4">
            <label className="block mb-2">Разновидность (необязательно)</label>
            <SearchableSelect
              options={availableSubtypes}
              value={subtypeId}
              onChange={(val) => setSubtypeId(val)}
              placeholder="— Разновидность —"
            />
          </div>
        )}

        {/* Manufacturer */}
        <div className="mt-4">
          <label className="block mb-2">Производитель</label>
          <SearchableSelect
            options={manufacturers}
            value={detail.manufacturer?.id || ''}
            onChange={(val) => {
              const parsed = typeof val === 'string' ? parseInt(val, 10) : val;
              setDetail(prev => ({
                ...prev,
                manufacturer: parsed ? { id: parsed } : null
              }));
            }}
            placeholder="— Производитель —"
          />
        </div>

        {/* Product */}
        <div className="mt-4">
          <label className="block mb-2">Изделие</label>
          <SearchableSelect
            options={products}
            value={detail.product?.id || ''}
            onChange={(val) => {
              const parsed = typeof val === 'string' ? parseInt(val, 10) : val;
              setDetail(prev => ({
                ...prev,
                product: parsed ? { id: parsed } : null
              }));
            }}
            placeholder="— К изделию —"
          />
        </div>

        {/* Stamps (native multiple) */}
        <div className="mt-6">
          <label className="block mb-2">Клейма</label>
          <select
            multiple
            value={detail.stamps?.map(s => s.id) || []}
            onChange={handleStampsChange}
            className="w-full p-2 bg-gray-800 rounded border border-gray-700 h-40"
          >
            {stamps.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        {/* Photos */}
        <div className="mt-6">
          <div className="mb-6">
            <PhotoUploader uploads={uploads} existing={detail.photos || []} onRemoveExisting={handleRemovePhoto} />
          </div>

          <div className="mb-6">
            <label className="block mb-2">Поиск существующих фото:</label>
            <div className="flex mb-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-grow p-2 bg-gray-800 rounded mr-2 border border-gray-700"
                placeholder="Поиск по URL или другим данным"
              />
              <button
                type="button"
                onClick={handleSearchPhotos}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
              >
                Найти
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {searchResults.map(photo => (
                <div key={photo.id} className="relative">
                  <ThumbnailImage
                    src={photo.url?.startsWith('/') ? `${uploadHost}${photo.url}` : photo.url}
                    className="w-24 h-24 object-cover rounded border border-gray-700"
                  />
                  <button
                    type="button"
                    onClick={() => handleAttachPhoto(photo)}
                    className="absolute bottom-0 right-0 bg-green-600 text-white rounded-full w-6 h-6 text-xs flex items-center justify-center"
                    title="Прикрепить фото"
                  >+</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Danger + Save */}
        <div className="mt-6 flex gap-2">
          <button
            type="button"
            onClick={async () => {
              if (window.confirm("Удалить эту деталь?")) {
                try {
                  await api.delete(`/details/${id}`);
                  navigate(-2);
                } catch (error) {
                  console.error("Ошибка при удалении:", error);
                  alert("Не удалось удалить деталь");
                }
              }
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
          >
            Удалить
          </button>

          <button
            type="submit"
            disabled={uploads.busy || saving}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-50 disabled:cursor-wait"
          >
            {saving ? 'Сохраняем…' : uploadButtonLabel(uploads, 'Сохранить')}
          </button>
        </div>
      </div>
    </form>
  );
}
